import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, CreditCard, UserX } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Panel, Empty } from "@/components/PageHeader";
import { periodLabel, useFilters } from "@/context/FilterContext";
import { api, eur, num, errMsg } from "@/lib/api";

const FMT = { pdf: "PDF", xlsx: "Excel", csv: "CSV" };
const it = (v) => (v ?? 0).toLocaleString("it-IT");
const COLS = [["name", "Punto vendita", false], ["card_receipts", "Scontr. carta", true], ["nocard_receipts", "Scontr. senza", true], ["card_spend", "Vendite carta", true],
  ["nocard_spend", "Vendite senza", true], ["card_avg", "Medio carta", true], ["nocard_avg", "Medio senza", true], ["share_spend", "% vendite carta", true]];

const download = async (fmt, setBusy) => {
  setBusy(fmt);
  try {
    const r = await api.get("/stats/fidelity/export", { params: { fmt }, responseType: "blob" });
    const name = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "")?.[1] || `report.${fmt}`;
    const url = URL.createObjectURL(r.data);
    Object.assign(document.createElement("a"), { href: url, download: name }).click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
};

const Side = ({ s, title, icon: Icon, tone, share, shareR }) => (
  <Panel testId={`fid-side-${tone}`}>
    <div className="p-5">
      <div className={`mb-4 flex items-center gap-2 text-sm font-semibold ${tone === "card" ? "text-blue-700" : "text-slate-600"}`}><Icon className="h-4 w-4" />{title}</div>
      <div className="grid grid-cols-2 gap-3">
        {[["Scontrini", num(s.receipts), `${it(shareR)}% del totale`], ["Vendite", eur(s.spend), `${it(share)}% del totale`],
          ["Scontrino medio", eur(s.avg_receipt), ""], ["Articoli / scontrino", it(s.items_per_receipt), `${num(s.items)} articoli`]].map(([l, v, sub]) => (
          <div key={l} className={`rounded-lg p-3 ${tone === "card" ? "bg-blue-50/60" : "bg-slate-50"}`}>
            <div className="text-[11px] uppercase tracking-wider text-slate-500">{l}</div>
            <div className="mt-1 font-heading text-lg font-semibold text-slate-900">{v}</div>
            {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
          </div>))}
      </div>
    </div>
  </Panel>
);

export default function FidelityCompare() {
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [sort, setSort] = useState("spend");
  const { filters } = useFilters();
  useEffect(() => { api.get("/stats/fidelity").then((r) => setD(r.data)).catch((e) => setError(errMsg(e))); }, []);

  const t = d?.totals;
  const stores = [...(d?.stores || [])].sort((a, b) => (typeof a[sort] === "string" ? a[sort].localeCompare(b[sort]) : (b[sort] ?? 0) - (a[sort] ?? 0)));
  return (
    <div>
      <PageHeader title="Carta vs senza carta" subtitle="Confronto tra movimenti con carta fedeltà e movimenti anonimi">
        <div className="flex flex-wrap items-center gap-2">
          <span data-testid="fid-period-label" className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600">Periodo: {periodLabel(filters)}</span>
          {Object.keys(FMT).map((f) => <Button key={f} size="sm" variant="outline" data-testid={`fid-export-${f}`} disabled={!d || !!busy} onClick={() => download(f, setBusy)}><Download className="mr-1.5 h-3.5 w-3.5" />{busy === f ? "..." : FMT[f]}</Button>)}
        </div>
      </PageHeader>
      {error && <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="fid-error">{error}</div>}
      {!d && !error && <Skeleton className="h-96" />}
      {d && (
        <div className="space-y-6">
          {d.demo && <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-700" data-testid="fid-demo-note">Modalità demo: i movimenti senza carta sono simulati.</div>}
          <div className="grid gap-4 lg:grid-cols-2">
            <Side s={t.card} title="Con carta fedeltà" icon={CreditCard} tone="card" share={t.share_spend} shareR={t.share_receipts} />
            <Side s={t.nocard} title="Senza carta fedeltà" icon={UserX} tone="nocard" share={Math.round((100 - t.share_spend) * 10) / 10} shareR={Math.round((100 - t.share_receipts) * 10) / 10} />
          </div>
          <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <Panel title={`Vendite per ${d.interval === "day" ? "giorno" : "mese"}`} testId="fid-trend-chart">
              <div className="h-72 p-3">
                <ResponsiveContainer>
                  <BarChart data={d.monthly}>
                    <CartesianGrid stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} />
                    <YAxis tickLine={false} axisLine={false} fontSize={10} width={70} tickFormatter={(v) => num(v)} />
                    <Tooltip formatter={(v) => eur(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="card_spend" name="Con carta" stackId="a" fill="#2563eb" />
                    <Bar dataKey="nocard_spend" name="Senza carta" stackId="a" fill="#cbd5e1" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
            <Panel title="Quota vendite con carta" testId="fid-share-chart">
              <div className="h-72 p-3">
                <ResponsiveContainer>
                  <LineChart data={d.monthly}>
                    <CartesianGrid stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} />
                    <YAxis tickLine={false} axisLine={false} fontSize={10} width={40} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                    <Tooltip formatter={(v) => `${it(v)}%`} />
                    <Line type="monotone" dataKey="share_spend" name="% con carta" stroke="#2563eb" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </div>
          <Panel title="Dettaglio per punto vendita" testId="fid-stores">
            {stores.length === 0 ? <Empty text="Nessun punto vendita nel periodo." /> : (
              <div className="max-h-[560px] overflow-auto text-[13px]">
                <Table data-testid="fid-stores-table">
                  <TableHeader><TableRow>
                    {COLS.map(([k, l, right]) => <TableHead key={k} className={`cursor-pointer whitespace-nowrap ${right ? "text-right" : ""}`} data-testid={`fid-sort-${k}`} onClick={() => setSort(k === "name" ? "name" : k)}><span className={sort === k ? "text-blue-600" : ""}>{l}</span></TableHead>)}
                  </TableRow></TableHeader>
                  <TableBody>
                    {stores.map((s) => (
                      <TableRow key={s.id} data-testid={`fid-store-row-${s.id}`}>
                        <TableCell className="max-w-[260px] truncate text-sm font-medium" title={s.name}>{s.name}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{num(s.card_receipts)}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{num(s.nocard_receipts)}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{eur(s.card_spend)}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{eur(s.nocard_spend)}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{eur(s.card_avg)}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{eur(s.nocard_avg)}</TableCell>
                        <TableCell className="text-right"><span className="rounded bg-blue-50 px-1.5 py-0.5 font-mono text-xs text-blue-700">{it(s.share_spend)}%</span></TableCell>
                      </TableRow>))}
                  </TableBody>
                </Table>
              </div>)}
          </Panel>
          <Panel title="Scontrini per giorno della settimana" testId="fid-weekday-chart">
            <div className="h-56 p-3">
              <ResponsiveContainer>
                <BarChart data={d.weekdays}>
                  <CartesianGrid stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="day" tickLine={false} axisLine={false} fontSize={11} />
                  <YAxis tickLine={false} axisLine={false} fontSize={10} width={60} tickFormatter={(v) => num(v)} />
                  <Tooltip formatter={(v) => num(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="card_receipts" name="Con carta" fill="#2563eb" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="nocard_receipts" name="Senza carta" fill="#cbd5e1" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}
